// EcommerceSEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'

const EcommerceSEOPage = () => {
    useEffect(() => {
        window.scroll(0, 0)
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const ecommerceStats = [
        { number: '46%', label: 'of all Google searches are for products' },
        { number: '35%', label: 'higher conversion rate from SEO vs paid ads' },
        { number: '68%', label: 'of online experiences begin with search engines' },
        { number: '5x', label: 'ROI from ecommerce SEO over 12 months' }
    ];

    const ecommerceApproaches = [
        {
            icon: 'fas fa-search',
            title: 'Ecommerce SEO Strategy & Research',
            desc: 'We analyze your market, competitive landscape, and how customers search for your products. Our approach includes keyword research for retail intent, competitor analysis, audience behavior insights, and a priority roadmap for optimization.'
        },
        {
            icon: 'fas fa-box-open',
            title: 'On-Page SEO For Products & Categories',
            desc: 'We optimize product and category pages with SEO-friendly titles, descriptions, meta tags, and schema markup. Our focus is on creating content that answers real buyer intent and drives conversions.'
        },
        {
            icon: 'fas fa-cogs',
            title: 'Technical SEO For Ecommerce',
            desc: 'We address unique technical challenges like crawlability, site speed, mobile-first architecture, and structured data implementation to ensure your ecommerce site performs efficiently in search.'
        },
        {
            icon: 'fas fa-shopping-cart',
            title: 'User Experience & Conversion Support',
            desc: 'We enhance navigation, internal linking, and user engagement signals to improve conversions. Our recommendations focus on keeping users engaged and moving them through the purchase funnel.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Platform-Specific Optimization',
            desc: 'We provide tailored solutions for Shopify, WooCommerce, Magento/Adobe Commerce, and other major ecommerce platforms to maximize their search potential and scalability.'
        },
        {
            icon: 'fas fa-tasks',
            title: 'Ongoing Optimization & Performance Tracking',
            desc: 'We monitor performance, measure impact, and evolve your strategy with KPI tracking, A/B testing recommendations, and continuous refinement based on real-world performance data.'
        }
    ];

    const benefits = [
        ['Higher Search Visibility', 'More customers find your store organically through targeted product and category rankings'],
        ['Increased Qualified Traffic', 'Visitors with genuine buying intent reach your product pages through relevant search queries'],
        ['Better Conversion Rates', 'Optimized user experience and targeted content leads to higher add-to-cart and checkout completion'],
        ['Stronger Brand Authority', 'Search engines recognize and prioritize your store as a trusted retailer in your niche'],
        ['Sustainable Revenue Growth', 'Long-term SEO strategy delivers measurable ROI and reduces reliance on paid advertising']
    ];

    const targetBusinesses = [
        'Retail brands aiming to scale organic sales and market share',
        'Online stores introducing new product lines or expanding to new markets',
        'Ecommerce sites struggling with visibility or ranking stagnation',
        'Businesses in highly competitive retail niches',
        'Stores seeking sustainable, cost-effective long-term growth',
        'Brands preparing for seasonal peaks and holiday shopping surges'
    ];

    const ecommerceQuestions = [
        'Are your product pages optimized for search intent and conversions?',
        'How can you outrank competitors for high-value retail keywords?',
        'Is your ecommerce platform configured for maximum search visibility?',
        'What technical barriers are preventing your products from ranking?',
        'How can you turn organic traffic into measurable revenue growth?'
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Ecommerce SEO</span> Services
                    </h1>
                    <p>Drive organic traffic, increase sales, and grow your online store</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    An <span className="sv-keyword-highlight">ecommerce SEO strategy</span> is specifically crafted for online retail success — boosting your store's search presence, attracting qualified traffic, and turning clicks into measurable revenue growth.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>Ecommerce SEO services</strong> focus on boosting your store's search presence, attracting qualified traffic, and turning clicks into measurable revenue growth. Organic search remains one of the most powerful long-term channels for online retailers.
                                </p>
                                <p className="sv-description">
                                    Our approach isn't just about getting traffic — it's about making that traffic business-relevant and ROI-driven. We combine technical expertise, audience insight, and data-backed strategies to optimize every part of your ecommerce presence.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Ecommerce SEO Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Ecommerce SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {ecommerceStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Ecommerce SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>Ecommerce SEO</strong> helps your store rank higher for product and category searches, increase organic traffic that converts into customers, improve user experience, and stay ahead of competitors. It's the practice of optimizing your online store to attract more qualified visitors through search engines.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our Ecommerce SEO Services Answer:</h4>
                                    {ecommerceQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Ecommerce SEO?</h2>
                                <p className="sv-section-description">
                                    Ecommerce businesses face intense competition and rapidly changing customer expectations. To win in search today, you need more than visibility — you need an SEO strategy specifically crafted for online retail success.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-search-dollar"></i>
                                        <h5>Search Visibility</h5>
                                        <p>Higher rankings for product and category searches drive more qualified traffic</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Conversion Rates</h5>
                                        <p>Optimized user experience leads to higher add-to-cart and checkout completion</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-trophy"></i>
                                        <h5>Competitive Edge</h5>
                                        <p>Outrank competitors in highly competitive retail niches</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-hand-holding-usd"></i>
                                        <h5>Better ROI</h5>
                                        <p>Sustainable growth that reduces reliance on paid advertising</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Ecommerce SEO Approach at Reachtix</h2>
                                <p className="sv-section-description">
                                    We combine technical expertise, audience insight, and data-backed strategies to optimize every part of your ecommerce presence for search and conversion.
                                </p>

                                <div className="sv-approaches-grid">
                                    {ecommerceApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Ecommerce SEO With Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in Ecommerce SEO?</h2>
                                <p className="sv-section-description">
                                    Our <strong>ecommerce SEO services</strong> deliver maximum value for businesses that want sustainable retail growth:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    We don't just attract traffic — we help businesses turn search visibility into revenue growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Supercharge Your Ecommerce Growth?</h2>
                                <p className="sv-cta-description">
                                    Organic search is one of your store's most valuable growth engines — if it's optimized correctly. Let Reachtix's Ecommerce SEO services shape a SEO roadmap that scales your traffic, improves conversions, and accelerates revenue.
                                </p>
                                <p className="sv-cta-description">
                                    Contact us to begin your ecommerce SEO journey today.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-shopping-cart mr-2"></i>
                                        Start Your SEO Journey
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default EcommerceSEOPage;